import React from "react";
import {
  Box,
  TextField,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  useTheme,
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
} from "@mui/material";
import SectionSubheading from "../SectionSubheading";
import UploadPictures from "../UploadPictures";

// Weather and Surface Preparation Options
const weatherOptions = [
  { label: "Clear", value: "clear" },
  { label: "Rain", value: "rain" },
  { label: "Cloudy", value: "cloudy" },
];

const surfacePrepOptions = [
  { label: "Manual", value: "manual" },
  { label: "Power Tool", value: "power_tool" },
  { label: "Abrasive Blast", value: "abrasive_blast" },
];

// Field configuration array
const WEATHER_TEMPERATURE_FIELDS = [
  {
    id: "weatherCondition",
    label: "Weather condition",
    type: "radio",
    required: true,
    options: weatherOptions,
    alignItems: "flex-start",
  },
  {
    id: "surfacePreparation",
    label: "Surface Preparation",
    type: "radio",
    required: true,
    options: surfacePrepOptions,
    alignItems: "flex-start",
  },
  {
    id: "typeOfAbrasive",
    label: "Type of Abrasive",
    type: "text",
    required: true,
    alignItems: "center",
  },
  {
    id: "surfaceProfile",
    label: "Surface Profile",
    type: "number",
    required: true,
    unit: "µm",
    alignItems: "center",
  },
  {
    id: "remarks",
    label: "Remarks",
    type: "textarea",
    required: true,
    alignItems: "flex-start",
  },
  {
    id: "pictures",
    label: "Upload Pictures",
    type: "upload",
    required: true,
    alignItems: "flex-start",
  },
];

/**
 * WeatherTemperatureConditionsForm - Reusable form component for weather and temperature conditions
 * @param {Object} values - Form values object containing the weather condition data
 * @param {Object} errors - Form errors object
 * @param {Object} touched - Form touched fields object
 * @param {Function} setFieldValue - Formik setFieldValue function
 * @param {boolean} viewOnly - Whether the form is in view-only mode
 */
const WeatherTemperatureConditionsForm = ({
  values,
  errors,
  touched,
  setFieldValue,
  viewOnly,
}) => {
  const theme = useTheme();

  // Styles following AmbientSurfaceConditionTable pattern
  const styles = {
    fieldRow: {
      display: "flex",
      mb: 2,
    },
    label: {
      width: { xs: 130, md: 160 },
      flexShrink: 0,
      color: theme.palette.text.primary,
      fontWeight: 500,
      fontSize: "0.875rem",
      mr: 2,
    },
    textField: {
      width: "100%",
      minWidth: 150,
      // maxWidth: 400,
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
      },
      "& .MuiInputBase-input": {
        fontSize: "0.875rem",
      },
      "& .MuiFormHelperText-root": {
        fontSize: "0.75rem",
        mt: 0.5,
      },
    },
  };

  const handleOptionChange = (fieldId, optionValue, checked) => {
    if (viewOnly) return;

    if (fieldId === "weatherCondition" || fieldId === "surfacePreparation") {
      // For radio fields: set single string value
      if (checked) setFieldValue(fieldId, optionValue);
    } else {
      // Fallback: array structure (not used currently)
      const currentValues = values[fieldId] || [];
      const newValue = checked
        ? [...currentValues, optionValue]
        : currentValues.filter((v) => v !== optionValue);

      setFieldValue(fieldId, newValue);
    }
  };

  const renderField = (field, hasError) => {
    switch (field.type) {
      case "radio":
        return (
          <Box>
            <FormControl error={!!hasError} component="fieldset">
              <RadioGroup
                value={values[field.id] || ""}
                onChange={(e) =>
                  handleOptionChange(field.id, e.target.value, true)
                }
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {field.options.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio disabled={viewOnly} />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {hasError && (
              <FormHelperText error>{errors[field.id]}</FormHelperText>
            )}
          </Box>
        );

      case "text":
      case "number":
        return (
          <TextField
            type={field.type}
            value={values[field.id] || ""}
            onChange={(e) =>
              setFieldValue(
                field.id,
                field.type === "number"
                  ? e.target.value === ""
                    ? ""
                    : parseFloat(e.target.value)
                  : e.target.value
              )
            }
            disabled={viewOnly}
            size="small"
            sx={styles.textField}
            error={hasError}
            helperText={hasError ? errors[field.id] : ""}
            InputProps={{
              ...(field.unit && {
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {field.unit}
                    </Box>
                  </InputAdornment>
                ),
              }),
            }}
          />
        );

      case "textarea":
        return (
          <TextField
            value={values[field.id] || ""}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            disabled={viewOnly}
            multiline
            minRows={2}
            size="small"
            sx={styles.textField}
            error={hasError}
            helperText={hasError ? errors[field.id] : ""}
          />
        );

      case "upload":
        return (
          <Box>
            <UploadPictures
              viewOnly={viewOnly}
              pictures={values[field.id] || []}
              onChange={(pictures) => setFieldValue(field.id, pictures)}
              maxImages={5}
              label=""
            />
            {hasError && (
              <FormHelperText error>{errors[field.id]}</FormHelperText>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <SectionSubheading title="Weather & Temperature Conditions" />
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Grid container spacing={2} sx={{ mt: 2, minWidth: 670 }}>
          {WEATHER_TEMPERATURE_FIELDS.map((field) => {
            const hasError = errors[field.id] && touched[field.id];

            return (
              <Grid
                key={field.id}
                size={{
                  xs:
                    field.type === "textarea" || field.type === "upload"
                      ? 12
                      : 6,
                  sm:
                    field.type === "textarea" || field.type === "upload"
                      ? 12
                      : 6,
                  md:
                    field.type === "textarea" || field.type === "upload"
                      ? 12
                      : 6,
                }}
              >
                <Box
                  sx={{
                    ...styles.fieldRow,
                    alignItems:
                      field.type === "radio" ? "flex-start" : "flex-start",
                  }}
                >
                  <Typography variant="body2" sx={styles.label}>
                    {field.label}
                    {field.required && (
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main, ml: 0.5 }}
                      >
                        *
                      </Box>
                    )}
                  </Typography>
                  <Box sx={{ width: "100%" }}>
                    {renderField(field, hasError)}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default WeatherTemperatureConditionsForm;
